import {arrayOf, oneOfType, shape, string} from 'prop-types';
import {
    ValidatedField,
    validateRequiredFields,
    hasErrors as hasValidationErrors,
    validatorShape,
} from '@sinclair-digital/sbg-storyline-components/models';
import {Validator, toValidatedField} from '@sinclair-digital/stl-common/src/ValidatedField';
import {PropertySiteV1} from '@sinclair-digital/stl-common/src/models/Property';
import {elems, get, set, modify} from 'partial.lenses';
import {pipe, prepend} from 'ramda';

export const providerNames = ['Gracenote', 'TitanTV'];

const gracenoteShape = {stationId: string};
const titantvShape = {registrationKey: string};

export interface CustomIntegrationPair {
    key: Validator<string>;
    val: string;
}

const customIntegrationPairsShape = {
    key: shape(validatorShape(string)),
    val: string,
};

const providerShape = {
    providerName: string,
    providerConfig: oneOfType([shape(gracenoteShape), shape(titantvShape)]),
};

interface GracenoteConfig {
    stationId: string;
}

interface TitanTVConfig {
    registrationKey: string;
}

export interface Provider {
    providerName: string;
    providerConfig: GracenoteConfig | TitanTVConfig;
}

export interface Integration {
    androidMorningApp: string;
    androidStationApp: string;
    androidWatchLiveApp: string;
    androidWeatherApp: string;
    customIntegrationPairs: CustomIntegrationPair[];
    dfpAdUnitId: Validator<string>;
    dfpNetworkId: Validator<string>;
    epgId: string;
    epgProviders: Provider[];
    googleAnalyticsId: Validator<string>;
    googleTagManagerId: string;
    iOSMorningApp: string;
    iOSStationApp: string;
    iOSWatchLiveApp: string;
    iOSWeatherApp: string;
    readAloudVoiceId?: string;
}

export const integrationOptics = {
    androidMorningApp: 'androidMorningApp',
    androidStationApp: 'androidStationApp',
    androidWatchLiveApp: 'androidWatchLiveApp',
    androidWeatherApp: 'androidWeatherApp',
    customIntegrationPairs: 'customIntegrationPairs',
    dfpAdUnitId: 'dfpAdUnitId',
    dfpNetworkId: 'dfpNetworkId',
    epgId: 'epgId',
    epgProviders: 'epgProviders',
    googleAnalyticsId: 'googleAnalyticsId',
    googleTagManagerId: 'googleTagManagerId',
    iOSMorningApp: 'iOSMorningApp',
    iOSStationApp: 'iOSStationApp',
    iOSWatchLiveApp: 'iOSWatchLiveApp',
    iOSWeatherApp: 'iOSWeatherApp',
};

export const defaultIntegrationSettings: Integration = {
    androidMorningApp: '',
    androidStationApp: '',
    androidWatchLiveApp: '',
    androidWeatherApp: '',
    customIntegrationPairs: [],
    dfpAdUnitId: toValidatedField(''),
    dfpNetworkId: toValidatedField(''),
    googleAnalyticsId: toValidatedField(''),
    googleTagManagerId: '',
    epgId: '',
    epgProviders: [],
    iOSMorningApp: '',
    iOSStationApp: '',
    iOSWatchLiveApp: '',
    iOSWeatherApp: '',
    readAloudVoiceId: '',
};

export const integrationSettingsShape = {
    dfpAdUnitId: shape(validatorShape(string)),
    dfpNetworkId: shape(validatorShape(string)),
    googleAnalyticsId: shape(validatorShape(string)),
    androidMorningApp: string,
    androidStationApp: string,
    androidWatchLiveApp: string,
    androidWeatherApp: string,
    customIntegrationPairs: arrayOf(shape(validatorShape(shape(customIntegrationPairsShape)))),
    epgId: string,
    epgProviders: arrayOf(shape(providerShape)),
    iOSMorningApp: string,
    iOSStationApp: string,
    iOSWatchLiveApp: string,
    iOSWeatherApp: string,
    readAloudVoiceId: string,
};

export const parseProviders = (str: string): Provider[] => {
    try {
        const jsonArr = JSON.parse(str);
        if (Array.isArray(jsonArr)) {
            return jsonArr;
        }
        return [];
    } catch (e) {
        return [];
    }
};

const parseCustomIntegrationPairs = (strOfPairs: string): CustomIntegrationPair[] => {
    try {
        const obj = JSON.parse(strOfPairs);
        return Object.keys(obj).map(k => ({key: toValidatedField(k), val: obj[k]}));
    } catch (e) {
        return [];
    }
};

/**
 * Convert server model into Integration
 */
export const parseIntegrationSettings = ({
    dfpAdUnitId,
    dfpNetworkId,
    googleAnalyticsId,
    integration: {
        androidMorningApp,
        androidStationApp,
        androidWatchLiveApp,
        androidWeatherApp,
        customIntegrationPairs,
        epgId,
        epgProviders,
        googleTagManagerId,
        iOSMorningApp,
        iOSStationApp,
        iOSWatchLiveApp,
        iOSWeatherApp,
        readAloudVoiceId,
    },
}: PropertySiteV1): Integration => ({
    androidMorningApp,
    androidStationApp,
    androidWatchLiveApp,
    androidWeatherApp,
    customIntegrationPairs: parseCustomIntegrationPairs(customIntegrationPairs),
    dfpAdUnitId: toValidatedField(dfpAdUnitId),
    dfpNetworkId: toValidatedField(dfpNetworkId),
    epgId,
    epgProviders: parseProviders(epgProviders),
    googleAnalyticsId: toValidatedField(googleAnalyticsId),
    googleTagManagerId: googleTagManagerId || '',
    iOSMorningApp,
    iOSStationApp,
    iOSWatchLiveApp,
    iOSWeatherApp,
    readAloudVoiceId: readAloudVoiceId || '',
});

const serializeCustomIntegrationPairs = (arrayOfPairs: CustomIntegrationPair[]) => {
    const arr = arrayOfPairs.reduce((acc, item: CustomIntegrationPair) => {
        acc[item.key.value] = item.val;
        return acc;
    }, {});
    return JSON.stringify(arr);
};

/**
 * @returns partial server property model
 */
export const serializeIntegration = ({
    googleAnalyticsId,
    dfpAdUnitId,
    dfpNetworkId,
    epgProviders,
    customIntegrationPairs,
    ...integration
}: Integration) => {
    return {
        integration: {
            ...integration,
            epgProviders: JSON.stringify(epgProviders),
            customIntegrationPairs: serializeCustomIntegrationPairs(customIntegrationPairs),
        },
        googleAnalyticsId: googleAnalyticsId.value,
        dfpAdUnitId: dfpAdUnitId.value,
        dfpNetworkId: dfpNetworkId.value,
    };
};

const requiredFields = [
    integrationOptics.dfpAdUnitId,
    integrationOptics.dfpNetworkId,
    integrationOptics.googleAnalyticsId,
];
const customIntegrationPairsKeys = [integrationOptics.customIntegrationPairs, elems, 'key'];

export const validatedFields = [...requiredFields, customIntegrationPairsKeys];
const pattern = /^[a-z_][a-z_\d]*$/i;

export const validateKey = keyValidatedField =>
    set(
        ValidatedField.errorMessage,
        pattern.test(get(ValidatedField.value)(keyValidatedField)) ? undefined : 'Invalid key',
    )(keyValidatedField);

export const validateUniqueKeys = pairs => {
    const test = {};
    return pairs.reduceRight((acc, el) => {
        if (test[el.key.value]) {
            return prepend(set(['key', ValidatedField.errorMessage], 'Duplicate')(el), acc);
        } else {
            test[el.key.value] = 1;
        }
        return prepend(el, acc);
    }, []);
};

// Integration -> Integration
export const validate = pipe(
    validateRequiredFields(requiredFields),
    modify(customIntegrationPairsKeys, validateKey),
    modify(integrationOptics.customIntegrationPairs, validateUniqueKeys),
);

export const hasIntegrationSettingsErrors = hasValidationErrors(validatedFields);
